/**
 * Plugin Configuration management page.
 * Aligned with SystemConfig page style and structure.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Upload,
  Checkbox,
  Select,
  Tag,
  Alert,
  Input as AntInput,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';

import { usePluginStore } from '@/store/pluginStore';
import { useConfigStore } from '@/store/configStore';
import apiClient from '@/api/client';
import { getThirdPartyPlugins } from '@/api/thirdPartyUpload';
import type { PluginConfig, PluginConfigCreate, ThirdPartyPlugin } from '@/types';

// 插件脚本仓库的公开地址（不含凭据），与后端 apps/plugins/repo_links.py 保持一致
const PLUGIN_SCRIPTS_REPO_URL = 'https://devops.momenta.works/Momenta/maf/_git/bvp_plugin_scripts';
const PLUGIN_SCRIPTS_REPO_BRANCH = 'dev';

interface FormConfig extends Omit<PluginConfigCreate, 'script_file' | 'input_kinds'> {
  script_file?: UploadFile[];
  input_kinds?: string | string[];
}

/**
 * 「第三方插件」栏 —— 只读目录（FR-006/FR-007）。
 * 插件须先开发并在后端注册才会出现，界面不提供创建/编辑/删除，也不展示任何接入参数。
 */
const ThirdPartyPluginSection: React.FC = () => {
  const [plugins, setPlugins] = useState<ThirdPartyPlugin[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getThirdPartyPlugins()
      .then(setPlugins)
      .catch(() => message.error('加载第三方插件失败'))
      .finally(() => setLoading(false));
  }, []);

  const columns: ColumnsType<ThirdPartyPlugin> = [
    { title: '插件名称', dataIndex: 'name', key: 'name', width: 160 },
    { title: '标识', dataIndex: 'code', key: 'code', width: 160 },
    {
      title: '支持的平台',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: '状态',
      dataIndex: 'is_available',
      key: 'is_available',
      width: 100,
      render: (available: boolean) =>
        available ? <Tag color="green">可用</Tag> : <Tag>已下架</Tag>,
    },
  ];

  // 作为页签内容返回裸表格：外层 Card 与 Tabs 由页面统一提供
  return (
    <Table
      columns={columns}
      dataSource={plugins}
      rowKey="id"
      loading={loading}
      pagination={false}
      locale={{ emptyText: '第三方插件需开发并注册后才会出现' }}
    />
  );
};

const PluginConfigPage: React.FC = () => {
  const { configs, loading, fetchConfigs, saveConfig, deleteConfig, error } = usePluginStore();
  const { projects, fetchProjects } = useConfigStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPluginTypeSelectOpen, setIsPluginTypeSelectOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PluginConfig | null>(null);
  const [form] = Form.useForm<FormConfig>();
  const [search, setSearch] = useState('');
  const [pluginTypes, setPluginTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedPluginType, setSelectedPluginType] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<number | undefined>(undefined);
  const [artifactKindOptions, setArtifactKindOptions] = useState<Array<{ label: string; value: string }>>([]);
  const lastFetchedProjectRef = useRef<number | undefined>(undefined);

  const fetchArtifactKinds = useCallback(async (projectId: number) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    try {
      const response = await apiClient.post('plugins/bat-rmp-query/', {
        action: 'get_artifact_kinds',
        project: project.name,
      });
      const kinds: Array<{ code: string; name: string }> = response.data?.data || [];
      if (kinds.length > 0) {
        setArtifactKindOptions(kinds.map(k => ({ label: k.name, value: k.code })));
      } else {
        setArtifactKindOptions([]);
      }
      lastFetchedProjectRef.current = projectId;
    } catch (err) {
      console.error('Failed to fetch artifact kinds:', err);
      setArtifactKindOptions([]);
    }
  }, [projects]);

  useEffect(() => {
    fetchConfigs().catch(err => {
      console.error('Failed to fetch configs:', err);
    });
    fetchProjects().catch(err => {
      console.error('Failed to fetch projects:', err);
    });
    // Fetch available plugin types
    fetchPluginTypes();
  }, [fetchConfigs, fetchProjects]);

  const fetchPluginTypes = async () => {
    try {
      const response = await apiClient.get('plugins/plugin_types/');
      setPluginTypes(response.data.available_types);
    } catch (err) {
      console.error('Failed to fetch plugin types:', err);
    }
  };

  const handleSubmit = async (values: FormConfig) => {
    try {
      const scriptFile = values.script_file?.[0]?.originFileObj;
      const scriptFileName = values.script_file?.[0]?.name;
      console.log('[PluginConfig.handleSubmit] values.script_file:', values.script_file);
      console.log('[PluginConfig.handleSubmit] scriptFile:', scriptFile);
      console.log('[PluginConfig.handleSubmit] scriptFileName:', scriptFileName);
      console.log('[PluginConfig.handleSubmit] scriptFile type:', scriptFile?.constructor?.name);
      const projectId = typeof values.project === 'number' ? values.project : parseInt(values.project as string, 10);

      const rawInputKinds = values.input_kinds;
      const inputKindsStr = Array.isArray(rawInputKinds) ? rawInputKinds.join(',') : (rawInputKinds || '');

      const isNotify = values.plugin_type === 'notify';
      // 脚本型插件（ota_package / conan_upload / notify）无 git 仓库与基线分支，
      // 不应填充 'main'。conan 的 SSH 接入信息在 manifest.yaml 的 env 里，不走界面。
      const isScriptOnly = values.plugin_type === 'ota_package'
        || values.plugin_type === 'conan_upload'
        || values.plugin_type === 'notify';

      const createData: PluginConfigCreate = {
        project: projectId,
        plugin_type: values.plugin_type || 'mcu_upload',
        git_url: isScriptOnly ? '' : (values.git_url || ''),
        base_branch: isScriptOnly ? '' : (values.base_branch || 'main'),
        is_active: values.is_active ?? true,
        input_kinds: inputKindsStr,
        auto_merge_mcu_pr: values.auto_merge_mcu_pr ?? false,
      };

      await saveConfig(editingConfig?.id || null, createData, isNotify ? undefined : scriptFile);
      message.success(editingConfig ? '更新成功' : '创建成功');
      setIsModalOpen(false);
      setEditingConfig(null);
      form.resetFields();
      await fetchConfigs();
    } catch (err) {
      const data = (err as any)?.response?.data;
      let errorMsg = editingConfig ? '更新失败' : '创建失败';

      if (data?.error) {
        errorMsg = data.error;
        if (data?.detail) {
          const detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
          errorMsg = `${errorMsg}：${detail}`;
        }
      } else if (data?.detail) {
        errorMsg = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
      }

      message.error(errorMsg);
    }
  };

  const handleDelete = (config: PluginConfig) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除"${config.project_name}"的插件配置吗？`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteConfig(config.id);
          message.success('删除成功');
          await fetchConfigs();
        } catch (err) {
          const errorMsg = (err as any)?.response?.data?.detail || '删除失败';
          message.error(errorMsg);
        }
      },
    });
  };

  const columns: ColumnsType<PluginConfig> = [
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
    },
    {
      title: '项目代码',
      dataIndex: 'project_code',
      key: 'project_code',
      width: 100,
      render: (code: string) => (code && !code.startsWith('__null_') ? code : ''),
    },
    {
      title: '插件类型',
      dataIndex: 'plugin_type_display',
      key: 'plugin_type',
      width: 100,
      filters: pluginTypes.map(pt => ({ text: pt.label, value: pt.value })),
      onFilter: (value, record) => record.plugin_type === value,
    },
    {
      title: 'Git仓库',
      dataIndex: 'git_url',
      key: 'git_url',
      render: (url: string, record: PluginConfig) => {
        if (record.plugin_type === 'ota_package') return '-';
        if (!url) return '-';
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url.split('/').pop()}
          </a>
        );
      },
    },
    {
      title: '基线分支',
      dataIndex: 'base_branch',
      key: 'base_branch',
      width: 100,
      render: (branch: string, record: PluginConfig) => {
        if (record.plugin_type === 'ota_package' || record.plugin_type === 'notify') return '-';
        return branch || '-';
      },
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: boolean) => (active ? '启用' : '禁用'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingConfig(record);
              const inputKindsVal = record.input_kinds
                  ? record.input_kinds.split(',').filter(Boolean)
                  : [];
              form.setFieldsValue({
                project: record.project,
                plugin_type: record.plugin_type,
                git_url: record.git_url,
                base_branch: record.base_branch,
                is_active: record.is_active,
                input_kinds: inputKindsVal,
                auto_merge_mcu_pr: record.auto_merge_mcu_pr ?? false,
              });
              setIsModalOpen(true);
              if (record.plugin_type === 'ota_package' || record.plugin_type === 'conan_upload') {
                fetchArtifactKinds(record.project);
              }
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const filteredConfigs = configs.filter(
    (c) =>
      (c.project_name.toLowerCase().includes(search.toLowerCase()) ||
        (c.project_code && !c.project_code.startsWith('__null_') && c.project_code.toLowerCase().includes(search.toLowerCase()))) &&
      (!selectedPluginType || c.plugin_type === selectedPluginType)
  );

  // 页签内容。与「系统配置」一致：外层单张 Card + Tabs，各页签只放列表内容。
  // 弹窗不放进页签 children —— 切换页签会卸载它，编辑中的表单会丢。
  const tabItems = [
    {
      key: 'flow',
      label: '流程插件',
      children: (
        <>
          <div className="mb-4 flex gap-2">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingConfig(null);
                form.resetFields();
                setArtifactKindOptions([]);
                lastFetchedProjectRef.current = undefined;
                setIsPluginTypeSelectOpen(true);
              }}
            >
              创建插件
            </Button>
            <Input.Search
              placeholder="搜索项目名称或代码"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 240 }}
            />
            <Select
              placeholder="筛选插件类型"
              allowClear
              style={{ width: 200 }}
              value={selectedPluginType}
              onChange={setSelectedPluginType}
              options={[
                { label: '所有类型', value: '' },
                ...pluginTypes,
              ]}
            />
          </div>

          <Table
            columns={columns}
            dataSource={filteredConfigs}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
          />
        </>
      ),
    },
    {
      key: 'thirdParty',
      label: '第三方插件',
      children: <ThirdPartyPluginSection />,
    },
  ];

  return (
    <Card title="插件配置">
      <Tabs items={tabItems} />

      {error && (
        <Modal
          title="错误"
          open={!!error}
          onOk={() => usePluginStore.getState().clearError()}
          okText="关闭"
          cancelButtonProps={{ style: { display: 'none' } }}
        >
          <p>{error}</p>
        </Modal>
      )}

      {/* Plugin Type Selection Modal */}
      <Modal
        title="选择插件类型和项目"
        open={isPluginTypeSelectOpen}
        onCancel={() => setIsPluginTypeSelectOpen(false)}
        onOk={() => {
          if (!selectedPluginType) {
            message.warning('请选择插件类型');
            return;
          }
          if (!selectedProject) {
            message.warning('请选择项目');
            return;
          }

          console.log('[PluginConfig] User selected pluginType:', selectedPluginType, 'project:', selectedProject);

          setIsPluginTypeSelectOpen(false);

          form.setFieldsValue({
            plugin_type: selectedPluginType,
            project: selectedProject,
          });
          setIsModalOpen(true);
          if (selectedPluginType === 'ota_package' || selectedPluginType === 'conan_upload') {
            fetchArtifactKinds(selectedProject);
          }
        }}
      >
        <Form layout="vertical">
          <Form.Item label="插件类型" required>
            <Select
              placeholder="选择插件类型"
              value={selectedPluginType}
              onChange={setSelectedPluginType}
              options={pluginTypes}
            />
          </Form.Item>
          <Form.Item label="项目" required>
            <Select
              placeholder="选择一个项目"
              value={selectedProject}
              onChange={setSelectedProject}
              options={projects.map((p) => ({
                label: p.name,
                value: p.id,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Generic Plugin Config Modal */}
      <Modal
        title={editingConfig ? '编辑插件配置' : '创建插件配置'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingConfig(null);
          setArtifactKindOptions([]);
          lastFetchedProjectRef.current = undefined;
        }}
        onOk={() => form.submit()}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(_changedValues, allValues) => {
            const currentPluginType = allValues.plugin_type || form.getFieldValue('plugin_type');
            const currentProject = allValues.project || form.getFieldValue('project');

            // Fetch artifact kinds when plugin_type is ota_package / conan_upload and project is set
            if ((currentPluginType === 'ota_package' || currentPluginType === 'conan_upload')
              && currentProject && lastFetchedProjectRef.current !== currentProject) {
              fetchArtifactKinds(currentProject);
            }
          }}
        >
          {/* 只读：脚本在插件仓库里的位置与最近一次推送。创建时无仓库信息，不展示。 */}
          {editingConfig && (
            <div
              style={{
                marginBottom: 16,
                padding: '12px 16px',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: '#8c8c8c', marginRight: 8 }}>插件脚本仓库</span>
                <a href={PLUGIN_SCRIPTS_REPO_URL} target="_blank" rel="noreferrer">
                  bvp_plugin_scripts
                </a>
                <Tag style={{ marginLeft: 8 }}>{PLUGIN_SCRIPTS_REPO_BRANCH}</Tag>
              </div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: '#8c8c8c', marginRight: 8 }}>当前脚本</span>
                {editingConfig.script_repo_path ? (
                  <a href={editingConfig.script_web_url} target="_blank" rel="noreferrer">
                    {editingConfig.script_repo_path}
                  </a>
                ) : (
                  <span style={{ color: '#bfbfbf' }}>未上传脚本</span>
                )}
              </div>
              <div>
                <span style={{ color: '#8c8c8c', marginRight: 8 }}>脚本最近推送</span>
                {editingConfig.last_push_commit ? (
                  <>
                    <a href={editingConfig.last_push_web_url} target="_blank" rel="noreferrer">
                      {editingConfig.last_push_commit_short || editingConfig.last_push_commit.slice(0, 7)}
                    </a>
                    {editingConfig.last_push_at && (
                      <span style={{ color: '#8c8c8c', marginLeft: 8 }}>
                        {dayjs(editingConfig.last_push_at).format('YYYY-MM-DD HH:mm')}
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{ color: '#bfbfbf' }}>未推送</span>
                )}
              </div>
              {editingConfig.plugin_type !== 'notify' && (
                <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 8 }}>
                  重新上传脚本会覆盖仓库中同一位置的文件
                </div>
              )}
            </div>
          )}

          <Form.Item
            label="插件类型"
            name="plugin_type"
            rules={[{ required: true, message: '请选择插件类型' }]}
            initialValue="mcu_upload"
            help="选择插件类型后上传对应的 Python 脚本。"
          >
            <Select
              placeholder="选择插件类型"
              disabled={!!editingConfig}
              options={pluginTypes}
            />
          </Form.Item>

          <Form.Item
            label="项目"
            name="project"
            rules={[{ required: true, message: '请选择项目' }]}
          >
            <Select
              placeholder="选择一个项目"
              disabled={!!editingConfig}
              options={projects.map((p) => ({
                label: p.name,
                value: p.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.plugin_type !== cur.plugin_type}
          >
            {({ getFieldValue }) => {
              const currentType = getFieldValue('plugin_type');
              const isOta = currentType === 'ota_package';
              const isNotify = currentType === 'notify';
              // conan_upload 也是脚本型：没有 git 仓库 / 基线分支，只有产物类型 + 脚本
              const isConan = currentType === 'conan_upload';
              // conan 需要 soc_mw，历史产物里未必有，兜底补上，免得选不到
              const conanKindOptions = artifactKindOptions.some(o => o.value === 'soc_mw')
                ? artifactKindOptions
                : [...artifactKindOptions, { label: 'soc_mw（SOC_MW / AP_demo 归档）', value: 'soc_mw' }];

              return (
                <>
                  {!isOta && !isNotify && !isConan && (
                    <>
                      <Form.Item
                        label="MCU Git仓库URL"
                        name="git_url"
                        rules={[
                          { required: true, message: '请输入MCU Git URL' },
                          { type: 'url', message: '请输入有效的URL' },
                        ]}
                      >
                        <AntInput placeholder="https://devops.momenta.works/Momenta/maf/_git/wys_mcu" />
                      </Form.Item>

                      <Form.Item
                        label="基线分支"
                        name="base_branch"
                        rules={[{ required: true, message: '请输入基线分支' }]}
                        initialValue="main"
                      >
                        <AntInput placeholder="main" />
                      </Form.Item>

                      <Form.Item
                        label="自动合入MCU PR"
                        name="auto_merge_mcu_pr"
                        valuePropName="checked"
                        initialValue={false}
                        help="勾选后，MCU 上传插件创建 PR 时将自动调用 Azure DevOps API 完成 PR 合入"
                      >
                        <Checkbox>创建 PR 后自动合入</Checkbox>
                      </Form.Item>
                    </>
                  )}

                  {isOta && (
                    <Form.Item
                      label="需处理的供应商产物"
                      name="input_kinds"
                      help={artifactKindOptions.length > 0
                        ? '选择 OTA 打包脚本需要处理哪些供应商产物包，流水线只会传递勾选的产物路径'
                        : '该项目暂无历史产物记录，请先选择项目后触发一次 BCR 流水线'}
                    >
                      <Checkbox.Group options={artifactKindOptions} />
                    </Form.Item>
                  )}

                  {isConan && (
                    <>
                      <Form.Item
                        label="需处理的供应商产物"
                        name="input_kinds"
                        help="conan 打包上传要处理哪些供应商产物（脚本读 SOC_MW_URL，通常勾 soc_mw）"
                      >
                        <Checkbox.Group options={conanKindOptions} />
                      </Form.Item>

                      <Form.Item label="SSH 接入信息">
                        <Alert
                          type="info"
                          showIcon
                          message="SSH_HOST / SSH_PORT / SSH_USER / REMOTE_TOOL_DIR 不在这个界面维护"
                          description="它们在 bvp_plugin_scripts 仓库的 manifest.yaml 里（conan 条目的 env，键名必须大写），SSH 密码由 BCR 框架侧注入。要改这些请直接改 manifest.yaml。"
                        />
                      </Form.Item>
                    </>
                  )}

                  {!isNotify && (
                    <Form.Item
                      label="上传脚本文件"
                      name="script_file"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => e.fileList}
                    >
                      <Upload.Dragger
                        maxCount={1}
                        accept=".py"
                        listType="text"
                        beforeUpload={() => false}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                          {isOta ? '点击或拖拽 OTA 打包 Python 脚本上传' : '点击或拖拽 Python 脚本上传'}
                        </p>
                        <p className="ant-upload-hint">仅支持 .py 脚本文件，大小不超过1MB</p>
                      </Upload.Dragger>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            label="激活此配置"
            name="is_active"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>启用该插件配置</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PluginConfigPage;
